import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import axios from 'axios';
import {useLocation, useNavigate} from 'react-router-dom';
import {FaMicrophone} from 'react-icons/fa';
import FoodCard from '../components/FoodCard';
import {TailSpin} from 'react-loader-spinner';
import {CartContext} from '../context/CartContext';
import {StoreContext} from '../context/StoreContext';
import filterIcon from '../assets/filter.svg';
import {IoSearch} from "react-icons/io5";
import {CART_MAPPING_PROMPT} from '../prompts/cartPrompt';
import {toast} from 'react-toastify';
import speechService from '../services/speechService';
import OtpAuthDialog from '../components/OtpAuthDialog';
import { IoTimeOutline } from 'react-icons/io5';
import { isRestaurantClosed, formatHourLabel, getClosingSoonMessage } from '../utils/restaurantHours';

const prioritizePromotedDishes = (items, dishIds) => {
  if (!Array.isArray(items) || items.length === 0 || !Array.isArray(dishIds) || dishIds.length === 0) {
    return items;
  }

  const promotedSet = new Set(dishIds);
  const promotedItemsMap = new Map();
  const remainingItems = [];

  items.forEach((item) => {
    if (promotedSet.has(item.dishId)) {
      if (!promotedItemsMap.has(item.dishId)) {
        promotedItemsMap.set(item.dishId, item);
      }
    } else {
      remainingItems.push(item);
    }
  });

  const orderedPromotedItems = dishIds
    .map((dishId) => promotedItemsMap.get(dishId))
    .filter(Boolean);

  return [...orderedPromotedItems, ...remainingItems];
};

const MenuItems = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pathList = pathname.split('/');
  const restaurantId = pathList[pathList.length - 1];
  const { hasItems } = useContext(CartContext);
  const { setOfferData, isBottomSheetOpen, addMultipleToCart, userId, accessToken } = useContext(StoreContext);

  const location = useLocation();
  const restaurant = location.state;
  const restaurantClosed = Boolean(restaurant && isRestaurantClosed(restaurant));
  const closingSoonLabel = restaurant && !restaurantClosed ? getClosingSoonMessage(restaurant) : null;
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const promotedDishIdsFromState = useMemo(() => {
    if (!restaurant) return [];
    if (Array.isArray(restaurant.promotedDishIds)) {
      return restaurant.promotedDishIds.filter(Boolean);
    }
    if (restaurant.promotedDishId) {
      return [restaurant.promotedDishId];
    }
    return [];
  }, [restaurant]);
  const promotedDishIdsFromUrl = useMemo(() => {
    const parseDishIds = (value) => {
      if (!value) return [];
      return value
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);
    };

    const multiple = parseDishIds(searchParams.get('promotedDishes'));
    const single = parseDishIds(searchParams.get('promotedDish'));

    return Array.from(new Set([...multiple, ...single]));
  }, [searchParams]);
  const promotedDishIds = useMemo(() => {
    const combined = [...promotedDishIdsFromState, ...promotedDishIdsFromUrl];
    return combined.filter((id, index) => id && combined.indexOf(id) === index);
  }, [promotedDishIdsFromState, promotedDishIdsFromUrl]);

  const [menuItems, setMenuItems] = useState([]);
  const [allMenuItems, setAllMenuItems] = useState([]); // source of truth for search filtering
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryList, setShowCategoryList] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const [, setSpokenText] = useState('');
  const [microphonePermission, setMicrophonePermission] = useState(null); // null = checking, true = granted, false = denied
  const categoryButtonRef = useRef(null);
  const categoryPopupRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const categorySectionRefs = useRef({});
  const [recommendedDishIds, setRecommendedDishIds] = useState([]);
  const [lovedDishIds, setLovedDishIds] = useState([]);
  const [defaultPriciestVariant, setDefaultPriciestVariant] = useState(false);

  const [showOtpDialog, setShowOtpDialog] = useState(false);

  const isVoiceOrderingEnabled = false;

  useEffect(() => {
    if (!restaurantId) return;
    if (!restaurant?.supportedServeTypes) return;
    try {
      localStorage.setItem("supportedServeTypesRestaurantId", restaurantId);
      localStorage.setItem(
        "supportedServeTypes",
        JSON.stringify(restaurant.supportedServeTypes)
      );
    } catch (e) {
      // ignore storage errors
    }
  }, [restaurant, restaurantId]);

  const featuredDishes = useMemo(() => {
    if (promotedDishIds.length === 0) {
      return [];
    }

    const menuItemsMap = new Map(menuItems.map((item) => [item.dishId, item]));
    return promotedDishIds
      .map((dishId) => menuItemsMap.get(dishId))
      .filter(Boolean);
  }, [menuItems, promotedDishIds]);

  const shouldShowFeaturedSection = featuredDishes.length > 0;
  const featuredSectionTitle = featuredDishes.length > 1 ? 'Featured Dishes' : 'Featured Dish';

  const recommendedDishes = useMemo(() => {
    if (recommendedDishIds.length === 0) {
      return [];
    }

    const menuItemsMap = new Map(menuItems.map((item) => [item.dishId, item]));
    const excludedIds = new Set(featuredDishes.map((dish) => dish.dishId));

    return recommendedDishIds
      .map((dishId) => menuItemsMap.get(dishId))
      .filter((dish) => dish && !excludedIds.has(dish.dishId));
  }, [menuItems, recommendedDishIds, featuredDishes]);

  const lovedDishes = useMemo(() => {
    if (lovedDishIds.length === 0) {
      return [];
    }

    const menuItemsMap = new Map(menuItems.map((item) => [item.dishId, item]));
    const excludedIds = new Set([
      ...featuredDishes.map((dish) => dish.dishId),
      ...recommendedDishes.map((dish) => dish.dishId)
    ]);

    return lovedDishIds
      .map((dishId) => menuItemsMap.get(dishId))
      .filter((dish) => dish && !excludedIds.has(dish.dishId));
  }, [menuItems, lovedDishIds, featuredDishes, recommendedDishes]);

  const shouldShowRecommendedSection = recommendedDishes.length > 0;
  const shouldShowLovedSection = lovedDishes.length > 0;

  // OpenAI API configuration
  const OPENAI_API_KEY = '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';
  const OPENAI_API_URL = 'https://api.openai.com/v1/chat/completions';

  // Check if speech recognition is supported (now includes iOS with Whisper)
  const isSpeechRecognitionSupported = () => {
    return speechService.isSpeechRecognitionSupported();
  };

  const isVoiceButtonVisible = isVoiceOrderingEnabled && !isBottomSheetOpen && isSpeechRecognitionSupported();
  const handleCheckout = () => {
    setOfferData(null);
    if (!userId || !accessToken) {
      setShowOtpDialog(true);
    } else {
      navigate('/checkout', {
        state: {
          name: restaurant.name,
          opensAt: restaurant.opensAt,
          closesAt: restaurant.closesAt,
        },
      });
    }
  };

  // Method to process voice input using OpenAI API
  const processVoiceInput = async (spokenText) => {
    // Show first toast with voice text for at least 3 seconds
    const voiceToastId = toast.info(
      <div className="flex flex-col items-center gap-1">
        <span>We heard you saying 👂🏼</span>
        <span className="font-bold">"{spokenText}"</span>
      </div>, 
      { 
        position: 'top-center',
        autoClose: false,
        closeButton: false
      }
    );
    
    // Ensure minimum 3 seconds display
    const startTime = Date.now();
    
    try {
      setIsProcessingVoice(true);
      
      // Prepare the prompt with menu data and user input (only available items)
      const availableItems = allMenuItems.filter(item => item.availableNow);
      const prompt = CART_MAPPING_PROMPT
        .replace('{{menu_json}}', JSON.stringify(availableItems.map(item => ({
          dishId: item.dishId,
          name: item.name,
          price: item.price,
          availableNow: item.availableNow
        }))))
        .replace('{{user_input}}', spokenText);

      // Call OpenAI API
      const response = await axios.post(OPENAI_API_URL, {
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'system',
            content: 'You are a helpful assistant that processes food orders and returns JSON data.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.1,
        max_tokens: 500
      }, {
        headers: {
          'Authorization': `Bearer ${OPENAI_API_KEY}`,
          'Content-Type': 'application/json'
        }
      });

      const content = response.data.choices[0].message.content;
      console.log('OpenAI response:', content);

            // Parse the JSON response
      let parsedItems;
      try {
        // Handle markdown-wrapped JSON responses (```json ... ```)
        let jsonContent = content;
        
        // Remove markdown code block formatting if present
        if (content.includes('```json')) {
          jsonContent = content.replace(/```json\s*/, '').replace(/\s*```/, '');
        } else if (content.includes('```')) {
          // Handle generic code blocks without language specification
          jsonContent = content.replace(/```\s*/, '').replace(/\s*```/, '');
        }
        
        // Try to extract JSON array from the cleaned content
        const jsonMatch = jsonContent.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          parsedItems = JSON.parse(jsonMatch[0]);
        } else {
          // Try parsing the entire cleaned content
          parsedItems = JSON.parse(jsonContent);
        }
      } catch (parseError) {
        console.error('Error parsing OpenAI response:', parseError);
        console.log('Raw response content:', content);

        // Close first toast and show error after minimum 3 seconds
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(3000 - elapsedTime, 0);
        
        setTimeout(() => {
          toast.dismiss(voiceToastId);
          toast.error('Can you please say it again', { position: 'top-center' });
        }, remainingTime);
        return;
      }

      // Add items to cart
      if (Array.isArray(parsedItems) && parsedItems.length > 0) {
        let addedCount = 0;
        let addedItems = [];
        let unavailableItems = [];

        try {
          // Add all items to cart in a single operation to avoid race conditions
          // Filter out unavailable items as an additional safety check
          const availableItems = [];
          
          parsedItems.forEach(item => {
            if (!item.dishId || !item.count || item.count <= 0) return;
            // Check if the dish is available
            const dish = allMenuItems.find(d => d.dishId === item.dishId);
            if (dish && dish.availableNow) {
              availableItems.push({
                dishId: item.dishId,
                count: item.count,
                variantId: null,
                addOnItems: []
              });
            } else {
              unavailableItems.push(item);
            }
          });
          
          const itemsToAdd = availableItems;

          if (itemsToAdd.length > 0) {
            await addMultipleToCart(itemsToAdd, restaurantId, restaurant.roadId);
            
            // Calculate totals and prepare display text
            for (const item of itemsToAdd) {
              addedCount += item.count;
              const dish = allMenuItems.find(d => d.dishId === item.dishId);
              if (dish) {
                addedItems.push(`${item.count}x ${dish.name}`);
              }
            }
          }
        } catch (error) {
          console.error('Error adding items to cart:', error);
          // Close first toast and show error after minimum 3 seconds
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(3000 - elapsedTime, 0);
          
          setTimeout(() => {
            toast.dismiss(voiceToastId);
            toast.error('Can you please say it again', { position: 'top-center' });
          }, remainingTime);
          return;
        }
        
        if (addedCount > 0) {
          const itemsText = addedItems.join(', ');
          let message = `Added to cart: ${itemsText}`;
          
          // Add message about unavailable items if any
          if (unavailableItems.length > 0) {
            const unavailableNames = unavailableItems.map(item => {
              const dish = allMenuItems.find(d => d.dishId === item.dishId);
              return dish ? dish.name : 'Unknown item';
            });
            message += `. Note: ${unavailableNames.join(', ')} not available.`;
          }
          
          // Close first toast and show success after minimum 3 seconds
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(3000 - elapsedTime, 0);
          
          setTimeout(() => {
            toast.dismiss(voiceToastId);
            toast.success(message, { 
              position: 'top-center',
              autoClose: 5000
            });
          }, remainingTime);
        } else {
          // Close first toast and show warning after minimum 3 seconds
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(3000 - elapsedTime, 0);
          
          setTimeout(() => {
            toast.dismiss(voiceToastId);
            const message = unavailableItems.length > 0 
              ? 'Requested items are not available. Please try different items.'
              : 'Can you please say it again';
            toast.warning(message, { position: 'top-center' });
          }, remainingTime);
        }
      } else {
        // Close first toast and show error after minimum 3 seconds
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(3000 - elapsedTime, 0);
        
        setTimeout(() => {
          toast.dismiss(voiceToastId);
          toast.error('Can you please say it again', { position: 'top-center' });
        }, remainingTime);
      }

    } catch (error) {
      console.error('Error processing voice input:', error);
      
      // Close first toast and show error after minimum 3 seconds
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(3000 - elapsedTime, 0);
      
      setTimeout(() => {
        toast.dismiss(voiceToastId);
        if (error.response?.status === 401) {
          toast.error('Error: Invalid API key. Please check your OpenAI configuration.', { position: 'top-center' });
        } else if (error.response?.status === 429) {
          toast.error('Error: Rate limit exceeded. Please try again later.', { position: 'top-center' });
        } else {
          toast.error('Can you please say it again', { position: 'top-center' });
        }
      }, remainingTime);
    } finally {
      setIsProcessingVoice(false);
    }
    };

  const handleSpeechRecognitionStart = async () => {
    console.log('Speech recognition started (button pressed)!');
    
    // Check if microphone permission is denied
    if (microphonePermission === false) {
      toast.warning(
        'Microphone permission is required for voice ordering. Please allow microphone access in your browser settings.',
        { 
          position: 'top-center',
          autoClose: 5000
        }
      );
      return;
    }

    // If permission hasn't been requested yet, request it now
    if (microphonePermission === null) {
      try {
        // Request microphone permission without starting recording
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Permission granted! Stop the test stream immediately
        stream.getTracks().forEach(track => track.stop());
        setMicrophonePermission(true);
        toast.success('Microphone access granted! Please hold the button again to start recording.', {
          position: 'top-center',
          autoClose: 3000
        });
        return; // Don't start recording yet, user needs to hold button again
      } catch (error) {
        // Permission denied
        setMicrophonePermission(false);
        toast.error('Microphone permission denied. Please allow microphone access in your browser settings.', {
          position: 'top-center',
          autoClose: 5000
        });
        return;
      }
    }

    // Permission is granted, start recording
    // Use the speech service to handle both browser and Whisper recognition
    speechService.startSpeechRecognition(
      // onResult callback
      async (transcript) => {
        console.log("User said:", transcript);
        setSpokenText(transcript);
        setIsListening(false);
        
        // Process the spoken text using OpenAI API
        await processVoiceInput(transcript);
      },
      // onError callback
      (error) => {
        console.error('Speech recognition error:', error);
        setIsListening(false);
        
        // Show user-friendly error messages
        let errorMessage = 'Speech recognition failed. Please try again.';
        if (error === 'no-speech') {
          errorMessage = 'No speech detected. Please try speaking again.';
        } else if (error === 'audio-capture') {
          setMicrophonePermission(false);
          errorMessage = 'Microphone access denied. Please allow microphone access in your browser settings.';
        } else if (error === 'not-allowed') {
          setMicrophonePermission(false);
          errorMessage = 'Microphone access denied. Please allow microphone access in your browser settings.';
        } else if (error === 'transcription-failed') {
          errorMessage = 'Failed to process your speech. Please try again.';
        }
        
        toast.error(errorMessage, { position: 'top-center', autoClose: 5000 });
      },
      // onStart callback
      () => {
        setIsListening(true);
        setMicrophonePermission(true); // Permission is granted if we got here
        setSpokenText(''); // Clear previous spoken text
        console.log('Speech recognition started...');
      },
      // onEnd callback
      () => {
        setIsListening(false);
      }
    );
  };

  const handleSpeechRecognitionStop = () => {
    console.log('Speech recognition stopped (button released)!');
    speechService.stopSpeechRecognition();
  };

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        const requestData = {
          type: 'COMPLETE',
          restaurantId: restaurantId,
          filters: [
            {
              type: 'AVAILABILITY',
              value: true
            }
          ]
        };

        const response = await axios.post(
          '/api/catalogue/restaurant/menu/v2',
          requestData,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        console.log(response);
        const responseData = response?.data?.data || {};
        const items = responseData.items || [];
        setAllMenuItems(items);
        setMenuItems(items);
        const recommendedIds = responseData.recommendedForYou || [];
        const lovedIds = responseData.lovedByAll || [];
        setRecommendedDishIds(Array.from(new Set(recommendedIds.filter(Boolean))));
        setLovedDishIds(Array.from(new Set(lovedIds.filter(Boolean))));
        // Parse categories
        const cats = responseData.categories || [];
        setCategories(cats);
        setDefaultPriciestVariant(Boolean(responseData.defaultPriciestVariant));
      } catch (err) {
        console.error('Error fetching menu items:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuItems();
  }, [restaurantId]);

  // Cleanup speech recognition on component unmount
  useEffect(() => {
    return () => {
      speechService.stopSpeechRecognition();
    };
  }, []);

  // Check microphone permission on mount (without requesting permission)
  useEffect(() => {
    // For browsers that support the Permissions API, we can check permission status without requesting it
    if (isSpeechRecognitionSupported() && navigator.permissions && navigator.permissions.query) {
      const checkMicrophonePermission = async () => {
        try {
          const result = await navigator.permissions.query({ name: 'microphone' });
          
          if (result.state === 'granted') {
            setMicrophonePermission(true);
          } else if (result.state === 'denied') {
            setMicrophonePermission(false);
          } else {
            // 'prompt' state means permission hasn't been requested yet
            setMicrophonePermission(null);
          }

          // Listen for permission changes
          result.addEventListener('change', () => {
            if (result.state === 'granted') {
              setMicrophonePermission(true);
            } else if (result.state === 'denied') {
              setMicrophonePermission(false);
            }
          });
        } catch (error) {
          // Permissions API not supported, we'll check on first tap
          setMicrophonePermission(null);
        }
      };

      checkMicrophonePermission();
    } else {
      // Permissions API not supported, we'll check on first tap
      setMicrophonePermission(null);
    }
  }, [restaurantId]);

  // Hide the keyboard as soon as the user starts scrolling (container or window).
  // Skip when the focused input lives inside a dialog (e.g. OTP login) so the
  // iOS viewport-resize scroll doesn't kill the keyboard.
  useEffect(() => {
    const blurActiveField = () => {
      const activeElement = document.activeElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') &&
        typeof activeElement.blur === 'function' &&
        !activeElement.closest('[role="dialog"]')
      ) {
        activeElement.blur();
      }
    };

    const container = scrollContainerRef.current;
    const listeners = [];

    if (container) {
      container.addEventListener('scroll', blurActiveField, { passive: true });
      listeners.push(() => container.removeEventListener('scroll', blurActiveField));
    }

    // Fallback for cases where the window scrolls instead of the container (mobile Safari)
    window.addEventListener('scroll', blurActiveField, { passive: true });
    window.addEventListener('touchmove', blurActiveField, { passive: true });
    listeners.push(() => window.removeEventListener('scroll', blurActiveField));
    listeners.push(() => window.removeEventListener('touchmove', blurActiveField));

    return () => {
      listeners.forEach((cleanup) => cleanup());
    };
  }, []);

  const scrollToCategory = (categoryId) => {
    if (!categoryId) return;
    const el = categorySectionRefs.current?.[categoryId];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    // Fallback: try by id if ref wasn't attached yet
    const byId = document.getElementById(`category-${categoryId}`);
    if (byId && typeof byId.scrollIntoView === 'function') {
      byId.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    const container = scrollContainerRef.current;
    if (container && typeof container.scrollTo === 'function') {
      container.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Filter menu items by search query (category selection scrolls instead of filtering)
  useEffect(() => {
    let filteredItems = allMenuItems;

    // Apply search filter
    if (searchQuery.trim()) {
      const terms = searchQuery
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean);

      filteredItems = filteredItems.filter((item) => {
        const name = (item.name || '').toLowerCase();
        return terms.every((term) => name.includes(term));
      });
    }

    const prioritizedFilteredItems = prioritizePromotedDishes(filteredItems, promotedDishIds);
    setMenuItems(prioritizedFilteredItems);
  }, [allMenuItems, searchQuery, promotedDishIds]);

  // Close category popup when clicking outside
  useEffect(() => {
    if (!showCategoryList) return;
    
    function handleClickOutside(event) {
      if (
        categoryPopupRef.current &&
        !categoryPopupRef.current.contains(event.target) &&
        categoryButtonRef.current &&
        !categoryButtonRef.current.contains(event.target)
      ) {
        setShowCategoryList(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCategoryList]);

  // loading logic
  if (loading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <TailSpin
          height="80"
          width="80"
          color="#FBC02D"
          ariaLabel="loading"
          radius="1"
          visible={true}
        />
      </div>
    );
  }

  // Check if restaurant is defined
  if (!restaurant) {
    return <div>No restaurant data available.</div>;
  }

  return (
    <>
      <div
        ref={scrollContainerRef}
        className='w-full h-screen flex flex-col font-sans pt-24 pb-48 items-center overflow-auto'
      >
        <div className='w-full max-w-6xl px-4 xl:px-6'>

          {restaurantClosed && (
            <div className='mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 flex items-center gap-3'>
              <span className='flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-red-100 text-red-600'>
                <IoTimeOutline className='h-5 w-5' />
              </span>
              <div>
                <p className='font-semibold text-red-700'>
                  {restaurant.name ? `${restaurant.name} is` : 'This restaurant is'} currently closed
                </p>
                <p className='text-sm text-red-600'>
                  {formatHourLabel(restaurant.opensAt)
                    ? `Opens again at ${formatHourLabel(restaurant.opensAt)}. You can browse the menu, but ordering is paused for now.`
                    : 'You can browse the menu, but ordering is paused for now.'}
                </p>
              </div>
            </div>
          )}

          {closingSoonLabel && (
            <div className='mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3'>
              <span className='flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 text-amber-600'>
                <IoTimeOutline className='h-5 w-5' />
              </span>
              <div>
                <p className='font-semibold text-amber-800'>
                  {restaurant.name ? `${restaurant.name} is` : 'This restaurant is'} closing soon
                </p>
                <p className='text-sm text-amber-700'>
                  {closingSoonLabel}
                  {formatHourLabel(restaurant.closesAt) ? ` (at ${formatHourLabel(restaurant.closesAt)})` : ''}.
                  {' '}You can still order, but please proceed with caution as it may not be fulfilled in time.
                </p>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="relative mb-6">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#FBC02D] focus:border-transparent"
              placeholder="Search dishes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          {shouldShowFeaturedSection && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl font-semibold text-[#171725]">{featuredSectionTitle}</h2>
                <span className="text-sm font-semibold text-[#FBC02D] uppercase tracking-wide">Billboard Special</span>
              </div>
              <div className="rounded-2xl border border-[#FBC02D] bg-[#FFF8E1] bg-opacity-70 px-4">
                <div className="space-y-4 py-4">
                  {featuredDishes.map((dish) => (
                    <FoodCard
                      key={dish.dishId}
                      name={dish.name}
                      price={dish.price}
                      rating={dish.rating}
                      ratingCounts={dish.ratingCounts}
                      thumbnailUrl={dish.thumbnailUrl}
                      dishId={dish.dishId}
                      restaurantId={restaurantId}
                      roadId={restaurant.roadId}
                      availableNow={dish.availableNow}
                      variations={dish.variations}
                      addOnGroups={dish.addOnGroups}
                      description={dish.description || dish.shortDescription || dish.itemDescription || ''}
                      foodPreference={dish.foodPreference}
                      defaultPriciestVariant={defaultPriciestVariant}
                      restaurantClosed={restaurantClosed}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {shouldShowRecommendedSection && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl font-semibold text-[#171725]">Recommended for You</h2>
              </div>
              <div className="rounded-2xl border border-[#FBC02D] bg-white px-4">
                <div className="space-y-4 py-4">
                  {recommendedDishes.map((dish) => (
                    <FoodCard
                      key={dish.dishId}
                      name={dish.name}
                      price={dish.price}
                      rating={dish.rating}
                      ratingCounts={dish.ratingCounts}
                      thumbnailUrl={dish.thumbnailUrl}
                      dishId={dish.dishId}
                      restaurantId={restaurantId}
                      roadId={restaurant.roadId}
                      availableNow={dish.availableNow}
                      variations={dish.variations}
                      addOnGroups={dish.addOnGroups}
                      description={dish.description || dish.shortDescription || dish.itemDescription || ''}
                      foodPreference={dish.foodPreference}
                      defaultPriciestVariant={defaultPriciestVariant}
                      restaurantClosed={restaurantClosed}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {shouldShowLovedSection && (
            <div className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl font-semibold text-[#171725]">Loved by All</h2>
              </div>
              <div className="rounded-2xl border border-[#FBC02D] bg-white px-4">
                <div className="space-y-4 py-4">
                  {lovedDishes.map((dish) => (
                    <FoodCard
                      key={dish.dishId}
                      name={dish.name}
                      price={dish.price}
                      rating={dish.rating}
                      ratingCounts={dish.ratingCounts}
                      thumbnailUrl={dish.thumbnailUrl}
                      dishId={dish.dishId}
                      restaurantId={restaurantId}
                      roadId={restaurant.roadId}
                      availableNow={dish.availableNow}
                      variations={dish.variations}
                      addOnGroups={dish.addOnGroups}
                      description={dish.description || dish.shortDescription || dish.itemDescription || ''}
                      foodPreference={dish.foodPreference}
                      restaurantClosed={restaurantClosed}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {menuItems.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 text-lg">No dishes found matching your search.</p>
              <p className="text-gray-500 mt-2">Try a different search term or clear the search.</p>
            </div>
          ) : categories.length > 0 ? (
            // Show categorized menu
            categories.map((category) => {
              const categoryItems = menuItems.filter(item =>
                category.dishes.includes(item.dishId)
              );

              if (categoryItems.length === 0) return null;

              return (
                <div
                  key={category.categoryId}
                  id={`category-${category.categoryId}`}
                  ref={(el) => {
                    if (el) categorySectionRefs.current[category.categoryId] = el;
                  }}
                  className="mb-12 scroll-mt-28"
                >
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-2xl font-semibold text-[#171725]">
                      {category.name}
                    </h2>
                    <span className="text-sm text-gray-500 font-medium">
                      {categoryItems.length} item{categoryItems.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {categoryItems.map((item) => (
                      <FoodCard
                        key={item.dishId}
                        name={item.name}
                        price={item.price}
                        rating={item.rating}
                        ratingCounts={item.ratingCounts}
                        thumbnailUrl={item.thumbnailUrl}
                        dishId={item.dishId}
                        restaurantId={restaurantId}
                        roadId={restaurant.roadId}
                        availableNow={item.availableNow}
                        variations={item.variations}
                        addOnGroups={item.addOnGroups}
                        description={item.description || item.shortDescription || item.itemDescription || ''}
                        foodPreference={item.foodPreference}
                        defaultPriciestVariant={defaultPriciestVariant}
                        restaurantClosed={restaurantClosed}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            // Show uncategorized menu
            <div className="space-y-2 mb-12">
              {menuItems.map((item) => (
                <FoodCard
                  key={item.dishId}
                  name={item.name}
                  price={item.price}
                  rating={item.rating}
                  ratingCounts={item.ratingCounts}
                  thumbnailUrl={item.thumbnailUrl}
                  dishId={item.dishId}
                  restaurantId={restaurantId}
                  roadId={restaurant.roadId}
                  availableNow={item.availableNow}
                  variations={item.variations}
                  addOnGroups={item.addOnGroups}
                  description={item.description || item.shortDescription || item.itemDescription || ''}
                  foodPreference={item.foodPreference}
                  defaultPriciestVariant={defaultPriciestVariant}
                  restaurantClosed={restaurantClosed}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Floating Checkout Button - Only show if there are items and no bottom sheet is open */}
      {hasItems && !isBottomSheetOpen && (
        <button
          onClick={handleCheckout}
          className="fixed bottom-8 right-8 bg-[#FBC02D] text-white rounded-full p-4 shadow-lg hover:bg-[#F9A825] transition-all duration-300 flex items-center gap-2 z-50"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 8l4 4m0 0l-4 4m4-4H3"
            />
          </svg>
        </button>
      )}
      {/* Floating Category Button - Only show if there are categories and no bottom sheet is open */}
      {categories.length > 0 && !isBottomSheetOpen && (
        <>
          <button
            ref={categoryButtonRef}
            onClick={() => {
              setShowCategoryList(!showCategoryList);
            }}
            className={`fixed bottom-8 ${hasItems
                ? (isVoiceButtonVisible ? 'right-48' : 'right-28')
                : (isVoiceButtonVisible ? 'right-28' : 'right-8')
            } bg-black text-white rounded-full p-4 shadow-lg flex items-center gap-2 z-50 min-w-[56px] min-h-[56px] justify-center transition-all duration-300`}
            style={{ fontWeight: 'bold', fontSize: '16px' }}
          >
            {selectedCategory
              ? categories.find(c => c.categoryId === selectedCategory)?.name
              : (
                <img src={filterIcon} alt="Filter" className="h-6 w-6" />
              )
            }
          </button>
          {/* Floating Category List */}
          {showCategoryList && (
            <div 
              ref={categoryPopupRef} 
              className={`fixed bottom-20 ${hasItems ? 'right-48' : 'right-28'} rounded-xl shadow-2xl z-50 p-2 min-w-[200px] max-h-[60vh] overflow-y-auto border border-gray-700 bg-gradient-to-br from-black via-gray-900 to-gray-800 bg-opacity-95 text-white animate-fade-in transition-all duration-300`}
            >
              {categories.map(cat => (
                <div
                  key={cat.categoryId}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-700 rounded ${selectedCategory === cat.categoryId ? 'bg-gray-800 font-bold' : ''}`}
                  onClick={() => {
                    setSelectedCategory(cat.categoryId);
                    setShowCategoryList(false);
                    scrollToCategory(cat.categoryId);
                  }}
                >
                  {cat.name}
                </div>
              ))}
              <div
                className="px-4 py-2 cursor-pointer text-red-400 hover:bg-gray-700 rounded"
                onClick={() => {
                  setSelectedCategory(null);
                  setShowCategoryList(false);
                  scrollToTop();
                }}
              >
                Show All
              </div>
            </div>
          )}
        </>
      )}
      {/* Floating Speech Recognition Button - Only show when no bottom sheet is open and speech recognition is supported */}
      {isVoiceButtonVisible && (
        <>
          {console.log('Rendering speech button, isBottomSheetOpen:', isBottomSheetOpen, 'hasItems:', hasItems)}
          <button
            onMouseDown={handleSpeechRecognitionStart}
            onMouseUp={handleSpeechRecognitionStop}
            onMouseLeave={handleSpeechRecognitionStop}
            onTouchStart={handleSpeechRecognitionStart}
            onTouchEnd={handleSpeechRecognitionStop}
            className={`fixed bottom-8 ${hasItems ? 'right-28' : 'right-8'} ${
              isListening 
                ? 'bg-red-600 animate-pulse scale-110' 
                : isProcessingVoice 
                  ? 'bg-yellow-600' 
                  : microphonePermission === false
                    ? 'bg-gray-500 hover:bg-gray-600'
                    : microphonePermission === null
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-blue-600 hover:bg-blue-700 active:bg-red-600'
            } text-white rounded-full p-4 shadow-lg transition-all duration-300 flex items-center gap-2 z-50 border-2 border-white group select-none`}
            disabled={isProcessingVoice}
            title={
              isListening 
                ? "Release to stop recording" 
                : microphonePermission === false
                  ? "Microphone permission required"
                  : microphonePermission === null
                    ? "Tap to allow microphone access"
                    : "Hold to order"
            }
          >
            {isProcessingVoice ? (
              <TailSpin height="20" width="20" color="white" ariaLabel="processing" />
            ) : (
              <FaMicrophone className="h-6 w-6" />
            )}
            {/* Tooltip */}
            <div className="absolute bottom-full right-0 mb-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap pointer-events-none">
              {isListening 
                ? "Release to stop" 
                : microphonePermission === false
                  ? "Allow microphone access"
                  : microphonePermission === null
                    ? "Tap to grant permission"
                    : "Hold to order"
              }
            </div>
          </button>
        </>
      )}

      <OtpAuthDialog
        isOpen={showOtpDialog}
        onClose={() => setShowOtpDialog(false)}
        onSuccess={() => {
          setShowOtpDialog(false);
          navigate('/checkout', {
            state: {
              name: restaurant.name,
              opensAt: restaurant.opensAt,
              closesAt: restaurant.closesAt,
            },
          });
        }}
      />
    </>
  );
};

export default MenuItems;
